import { Box, Fab, List, Stack, Typography } from '@mui/material'
import { Fragment, useCallback, useState } from 'react'
import { Divider, Icons } from 'utoniq-ui'

import {
  ArtistCreatePostCategoryInput,
  ArtistRemovePostCategoryInput,
  ArtistUpdatePostCategoryInput,
  PostCategoryDetail,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { CategoryBox } from './categoryBox'
import { ConsolePostCategoriesSkelton } from './conslePostCategoriesSkeleton'
import { CreatePostCategoryModal } from './createPostCategoryModal'

export * from './fragments'

export type State = {
  artistId: string
  categories: PostCategoryDetail[]
  loading: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onCreateCategory: (inputs: ArtistCreatePostCategoryInput) => Promise<void>
  onUpdateCategory: (inputs: ArtistUpdatePostCategoryInput) => Promise<void>
  onDeleteCategory: (inputs: ArtistRemovePostCategoryInput) => Promise<void>
}

export const ConsolePostCategoriesTemplate: Template<State, Action> = ({
  state,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
}) => {
  const { t } = useTranslation('consolePostCategories')
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsolePostCategories} />
      <ConsoleBaseLayout
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: routerPages.ConsolePost,
        }}
        margin
        hasConsoleFooter
      >
        {state.loading ? (
          <>
            <ConsolePostCategoriesSkelton />
          </>
        ) : (
          <>
            {state.categories.length ? (
              <CellFrame sx={{ pt: 6, mb: 6 }}>
                <Typography variant="h3">{t('postCategories')}</Typography>
                <CategoryListCell
                  categories={state.categories}
                  onUpdateCategory={onUpdateCategory}
                  onDeleteCategory={onDeleteCategory}
                />
              </CellFrame>
            ) : (
              <Stack py={6}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  {t('emptyCategoryMessage', 'ポストカテゴリがありません')}
                </Typography>
              </Stack>
            )}
            <BaseCell onCreateCategory={onCreateCategory} />
          </>
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type BaseCellProps = {
  onCreateCategory: (inputs: ArtistCreatePostCategoryInput) => Promise<void>
}
const BaseCell: Cell<BaseCellProps> = ({ onCreateCategory }) => {
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 100,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab size="medium" color="primary" onClick={toggleDrawer(true)}>
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <CreatePostCategoryModal
        open={open}
        onClose={toggleDrawer(false)}
        onCreateCategory={onCreateCategory}
      />
    </Stack>
  )
}

type CategoryListCellProps = {
  categories: PostCategoryDetail[]
  onUpdateCategory: (inputs: ArtistUpdatePostCategoryInput) => Promise<void>
  onDeleteCategory: (inputs: ArtistRemovePostCategoryInput) => Promise<void>
}

const CategoryListCell: Cell<CategoryListCellProps> = ({
  categories,
  onUpdateCategory,
  onDeleteCategory,
}) => {
  return (
    <CellFrame>
      <Box sx={{ pb: 2 }}>
        <List>
          {categories.map((category, index) => (
            <Fragment key={category.id}>
              <CategoryBox
                category={category}
                divider
                onUpdateCategory={onUpdateCategory}
                onDeleteCategory={onDeleteCategory}
              />
              {categories.length - 1 > index && <Divider sx={{ mx: 5 }} />}
            </Fragment>
          ))}
        </List>
      </Box>
    </CellFrame>
  )
}
